/**
 * Trakt (Settings › Trakt): the account connects its Trakt account with a
 * code typed at Trakt, and from then on the broker keeps the two watch
 * histories in step — plays Trakt holds come into the account's watch
 * state, plays made on Rago go to Trakt — as often as the account chose,
 * or when it presses Sync now. Marking something unwatched never
 * travels, in either direction. Runtime validators behind
 * contracts/openapi/web-broker.v1.yaml (/v1/me/trakt…).
 */
import { z } from "zod";

/** A connection being made: what the account types at Trakt, and until when. */
export const traktPendingSchema = z.object({
  userCode: z.string(),
  /** Where the code is typed — Trakt's own answer, never assumed. */
  verificationUrl: z.string(),
  /** When Trakt issued the code: with `expiresAt`, how much of its life is left. */
  issuedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  /** Seconds between polls of POST /v1/me/trakt/link/poll. */
  intervalS: z.number().int().min(1),
});
export type TraktPending = z.infer<typeof traktPendingSchema>;

export const traktSyncPhaseSchema = z.enum(["idle", "reading", "importing", "exporting"]);
export type TraktSyncPhase = z.infer<typeof traktSyncPhaseSchema>;

/**
 * Why the last sync did not finish, when it did not:
 *  - trakt_unreachable: Trakt did not answer, or not usably; retried by itself
 *  - rate_limited: Trakt asked for a pause; retried by itself
 *  - account_limit: the Trakt account is at one of Trakt's limits — nothing more is sent
 *  - locked: Trakt has locked the account
 */
export const traktSyncErrorSchema = z.enum([
  "trakt_unreachable",
  "rate_limited",
  "account_limit",
  "locked",
]);
export type TraktSyncError = z.infer<typeof traktSyncErrorSchema>;

/**
 * How often an account may have the sync run by itself, in seconds: hourly,
 * every 6 hours, daily, weekly. The fifth choice is null — never, only
 * Sync now.
 */
export const TRAKT_SYNC_INTERVALS_S = [3600, 21600, 86400, 604800] as const;

export const traktLinkSchema = z.object({
  username: z.string(),
  /** trakt.tv profile, when Trakt named one. */
  profileUrl: z.string().nullable(),
  vip: z.boolean(),
  /** reauth_required: Trakt no longer accepts the stored tokens; connect again. */
  status: z.enum(["active", "reauth_required"]),
  /** The two directions. Both off on a new connection; with both off nothing syncs, Sync now included. */
  importEnabled: z.boolean(),
  exportEnabled: z.boolean(),
  /** Seconds between the syncs that run by themselves, both ways (a day on a new connection); null = only Sync now. */
  syncIntervalS: z.number().int().min(1).nullable(),
  connectedAt: z.iso.datetime(),
  /** The last sync that finished; null before the first. */
  lastSyncAt: z.iso.datetime().nullable(),
  lastError: traktSyncErrorSchema.nullable(),
  sync: z.object({
    phase: traktSyncPhaseSchema,
    /** Pages read, plays imported or plays sent, by phase; 0/0 while idle. */
    done: z.number().int().min(0),
    total: z.number().int().min(0),
    /** A sync is due or was asked for and has not started; never with both directions off. */
    queued: z.boolean(),
  }),
  totals: z.object({
    imported: z.number().int().min(0),
    exported: z.number().int().min(0),
    /** Plays Trakt could not match to a title it knows. */
    notFound: z.number().int().min(0),
  }),
});
export type TraktLink = z.infer<typeof traktLinkSchema>;

/** GET /v1/me/trakt. */
export const traktStateSchema = z.object({
  /** False when the broker has no Trakt app configured. */
  available: z.boolean(),
  /** The account's "Sync my watch state" consent, which Trakt sync needs. */
  watchStateSync: z.boolean(),
  pending: traktPendingSchema.nullable(),
  link: traktLinkSchema.nullable(),
});
export type TraktState = z.infer<typeof traktStateSchema>;

/** POST /v1/me/trakt/link/poll. */
export const traktLinkPollSchema = z.object({
  /**
   *  - pending: not approved yet, keep polling
   *  - connected: approved; `state` carries the new link
   *  - denied: the account said no at Trakt
   *  - expired: the code is over (or there was none) — start again
   */
  status: z.enum(["pending", "connected", "denied", "expired"]),
  state: traktStateSchema,
});
export type TraktLinkPoll = z.infer<typeof traktLinkPollSchema>;

export const patchTraktSchema = z
  .object({
    importEnabled: z.boolean().optional(),
    exportEnabled: z.boolean().optional(),
    /** One of TRAKT_SYNC_INTERVALS_S, or null to stop the automatic sync. */
    syncIntervalS: z
      .union(TRAKT_SYNC_INTERVALS_S.map((s) => z.literal(s)))
      .nullable()
      .optional(),
  })
  .refine(
    (v) =>
      v.importEnabled !== undefined ||
      v.exportEnabled !== undefined ||
      v.syncIntervalS !== undefined,
    { message: "Nothing to patch" },
  );
export type PatchTrakt = z.infer<typeof patchTraktSchema>;
